/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */
import { ColumnDef } from '@tanstack/react-table';
import { Antrag, Arbeitsbereich, Arbeitszeit, Mitarbeiter } from '../entities';
import { useTranslation } from 'react-i18next';
import {
  ArbeitsbereichCell,
  ArbeitszeitCell,
  DauerCell,
  DienstplanuebernahmeTableRow,
  MitarbeiterCell,
} from '../components';
import {
  filterArbeitsbereiche,
  filterMitarbeiterName,
} from '../filterFunctions';
import { calculateAbweichung } from '../../../utils/helperFunctions';
import { EditCell } from '../components/Cells/EditCell';
import { AbwesenheitsCell } from '../components/Cells/AbwesenheitsCell';

import { SortableHeader } from '../components/Table';
import { ArbeitsbereichFilter, DataTableRow } from '../DataTable';
import {
  TerminTooltip,
  BemerkungTooltip,
  RechtshinweiseTooltip,
  ZuschlaegeTooltip,
} from '../components/Tooltips';
import { E2NTableCell } from '../../../components/Table';
import { ArbeitszeitAntragCell } from '../components/Cells/ArbeitszeitAntragCell';
import { DienstplanuebernahmeArbeitszeit } from '../components/Dienstplanuebernahme/DienstplanuebernahmeArbeitszeit';
import { DienstplanuebernahmeEdit } from '../components/Dienstplanuebernahme/DienstplanuebernahmeEdit';
import ArbeitszeitTooltipsCell from './tooltips';
import { EditAntragCell } from '../components/Cells/EditAntragCell';
import { ArbeitszeitArbeitszeitantragStatus } from '@/gql/graphql';
import dayjs from 'dayjs';
import { mapAbwesenheitsGrundToIcon } from '@/domain/arbeitszeiten/shared/util/mapAbwesenheitToIcon';

export function useColumns(): {
  abgeschlosseneArbeitszeiten: ColumnDef<Arbeitszeit>[];
  offeneArbeitszeiten: ColumnDef<DataTableRow>[];
} {
  const { t } = useTranslation();

  const mitarbeiterColumnDef: ColumnDef<DataTableRow> = {
    accessorKey: 'mitarbeiter',
    cell: (props) => {
      const row = props.row.original;
      const mitarbeiter = row.mitarbeiter;
      return <MitarbeiterCell mitarbeiter={mitarbeiter} />;
    },
    header: ({ column }) => (
      <SortableHeader
        title={t('entities.name_one')}
        sorted={column.getIsSorted()}
      />
    ),
    filterFn: (row, _, filterValue) => {
      const original = row.original;

      return filterMitarbeiterName(
        original.mitarbeiter.vorname,
        original.mitarbeiter.nachname,
        filterValue,
      );
    },

    sortingFn: (rowA, rowB) => {
      const mitarbeiternameA =
        rowA.original.mitarbeiter.vorname + rowA.original.mitarbeiter.nachname;
      const mitarbeiternameB =
        rowB.original.mitarbeiter.vorname + rowB.original.mitarbeiter.nachname;
      return mitarbeiternameA.localeCompare(mitarbeiternameB);
    },
  };

  const arbeitsbereichColumnDef: ColumnDef<DataTableRow> = {
    accessorKey: 'arbeitsbereich',
    cell: (props) => {
      const arbeitsbereich = props.getValue() as Arbeitsbereich;

      return <ArbeitsbereichCell arbeitsbereich={arbeitsbereich} />;
    },

    filterFn: (row, _, filterValue: ArbeitsbereichFilter) => {
      return filterArbeitsbereiche(
        row.original.arbeitsbereich.id,
        row.original.arbeitsbereich.abteilungen,
        filterValue,
        filterValue.arbeitsbereiche,
        filterValue.abteilungen,
      );
    },
    header: ({ column }) => (
      <SortableHeader
        title={t('entities.department_one')}
        sorted={column.getIsSorted()}
      />
    ),
    sortingFn: (rowA, rowB) => {
      const arbeitsbereichA =
        rowA.original.arbeitsbereich.name + rowA.original.arbeitsbereich.id;
      const arbeitsbereichB =
        rowB.original.arbeitsbereich.name + rowB.original.arbeitsbereich.id;
      return arbeitsbereichA.localeCompare(arbeitsbereichB);
    },
  };

  const arbeitszeitColumnDef: ColumnDef<DataTableRow> = {
    accessorKey: 'arbeitszeit',
    cell: (props) => {
      const arbeitszeit = props.row.original;

      if (
        'antraege' in arbeitszeit &&
        arbeitszeit.antraege.length > 0 &&
        arbeitszeit.antraege.find(
          (antrag) =>
            ((antrag.status !== ArbeitszeitArbeitszeitantragStatus.Abgelehnt &&
              dayjs(arbeitszeit.ende).isSame(antrag.ende.time)) ||
              arbeitszeit.ende === null) &&
            ((antrag.status !== ArbeitszeitArbeitszeitantragStatus.Abgelehnt &&
              dayjs(arbeitszeit.start).isSame(antrag.beginn.time)) ||
              arbeitszeit.start === null),
        )
      ) {
        const sortedAntraege = arbeitszeit.antraege.sort(
          (a, b) =>
            new Date(b.erstellungsdatum).getTime() -
            new Date(a.erstellungsdatum).getTime(),
        );
        return (
          <ArbeitszeitAntragCell
            beginn={sortedAntraege[0]?.beginn}
            ende={sortedAntraege[0]?.ende}
            dauer={arbeitszeit.pausendauer}
            status={sortedAntraege[0]?.status}
            antragId={sortedAntraege[0]?.id}
            arbeitszeitId={sortedAntraege[0]?.arbeitszeitId}
            pausen={arbeitszeit.pausen}
          />
        );
      }

      if ('arbeitszeitId' in arbeitszeit && arbeitszeit.offen) {
        return (
          <ArbeitszeitAntragCell
            beginn={arbeitszeit.beginn}
            ende={arbeitszeit.ende}
            dauer={0}
            status={arbeitszeit.status}
            antragId={arbeitszeit.id}
            pausen={[]}
          />
        );
      }

      if (arbeitszeit instanceof Arbeitszeit) {
        if (arbeitszeit.abwesenheit !== null) {
          return (
            <AbwesenheitsCell
              icon={mapAbwesenheitsGrundToIcon(arbeitszeit.abwesenheit.grund)}
              grund={arbeitszeit.abwesenheit.grund.toLocaleUpperCase()}
            />
          );
        }

        return (
          <ArbeitszeitCell
            beginn={arbeitszeit.start}
            ende={arbeitszeit.ende}
            dauer={arbeitszeit.pausendauer ?? 0}
            puenktlichkeit={arbeitszeit.puenktlichkeit}
            kommen={arbeitszeit.kommen}
            gehen={arbeitszeit.gehen}
            pausen={arbeitszeit.pausen}
          />
        );
      }
    },
    header: ({ column }) => (
      <SortableHeader
        title={t('entities.workingHour_one')}
        sorted={column.getIsSorted()}
      />
    ),
    sortingFn: (rowA, rowB) => {
      const startA = (rowA.original as Arbeitszeit).start;
      const startB = (rowB.original as Arbeitszeit).start;
      if (startA === null) {
        return 1;
      }
      if (startB === null) {
        return -1;
      }
      if (startA < startB) {
        return -1;
      }
      if (startA > startB) {
        return 1;
      }
      return 0;
    },
  };

  const dauerColumnDef: ColumnDef<DataTableRow> = {
    accessorKey: 'dauer',
    header: ({ column }) => (
      <SortableHeader
        title={t('entities.duration')}
        sorted={column.getIsSorted()}
      />
    ),
    sortingFn: (rowA, rowB) => {
      const arbeitszeitA = rowA.original as Arbeitszeit;
      const arbeitszeitB = rowB.original as Arbeitszeit;

      let dauerA = arbeitszeitA.dauer;
      let dauerB = arbeitszeitB.dauer;

      if (arbeitszeitA.abwesenheit !== null) {
        dauerA = arbeitszeitA.abwesenheit.dauer;
      }
      if (arbeitszeitB.abwesenheit !== null) {
        dauerB = arbeitszeitB.abwesenheit.dauer;
      }

      if (dauerA < dauerB) {
        return -1;
      }
      if (dauerA > dauerB) {
        return 1;
      }
      return 0;
    },
    cell: (props) => {
      const arbeitszeit = props.row.original;

      if (!(arbeitszeit instanceof Arbeitszeit)) {
        return <DauerCell dauer={0} isBorderVisible={true} />;
      }

      if (arbeitszeit.abwesenheit !== null) {
        return (
          <DauerCell
            dauer={arbeitszeit.abwesenheit.dauer}
            isBorderVisible={false}
          />
        );
      }

      let dauerCell = (
        <DauerCell dauer={arbeitszeit.dauer} isBorderVisible={true} />
      );

      if (
        arbeitszeit.puenktlichkeit.abweichungBeginn !== undefined &&
        arbeitszeit.puenktlichkeit.abweichungEnde !== undefined
      ) {
        const abweichung = calculateAbweichung(
          arbeitszeit.puenktlichkeit.abweichungBeginn,
          arbeitszeit.puenktlichkeit.abweichungEnde,
        );
        dauerCell = (
          <DauerCell
            dauer={arbeitszeit.dauer}
            abweichung={abweichung}
            isBorderVisible={true}
          />
        );
      }

      return dauerCell;
    },
  };

  const tooltipsColumnDef: ColumnDef<DataTableRow> = {
    accessorKey: 'tooltips',
    header: '',
    cell: (props) => {
      const arbeitszeit = props.row.original;

      if (
        arbeitszeit instanceof Arbeitszeit &&
        arbeitszeit.abwesenheit === null
      ) {
        return <ArbeitszeitTooltipsCell arbeitszeit={arbeitszeit} />;
      }
      return <E2NTableCell className="font-semibold w-auto"></E2NTableCell>;
    },
  };

  const editButtonColumnDef: ColumnDef<DataTableRow> = {
    id: 'editButton',
    header: '',
    cell: (props) => {
      const arbeitszeit = props.row.original;

      if (
        arbeitszeit instanceof Arbeitszeit &&
        arbeitszeit.abwesenheit !== null
      ) {
        return <E2NTableCell className="rounded-r-lg w-40 p-4"></E2NTableCell>;
      }

      if (arbeitszeit instanceof Arbeitszeit) {
        const hasOpenAntrag = arbeitszeit.antraege?.some(
          (antrag) => antrag.offen,
        );

        if (!hasOpenAntrag) {
          return <EditCell arbeitszeit={arbeitszeit} />;
        }

        if (hasOpenAntrag) {
          return <EditAntragCell arbeitszeit={arbeitszeit} />;
        }
      }

      if (arbeitszeit instanceof Antrag && arbeitszeit.offen) {
        return <EditAntragCell arbeitszeit={arbeitszeit} />;
      }

      return <E2NTableCell className="rounded-r-lg w-40 p-4"></E2NTableCell>;
    },
  };

  return {
    abgeschlosseneArbeitszeiten: [
      mitarbeiterColumnDef,
      arbeitsbereichColumnDef,
      arbeitszeitColumnDef,
      dauerColumnDef,
      tooltipsColumnDef,
      editButtonColumnDef,
    ] as ColumnDef<Arbeitszeit>[],

    offeneArbeitszeiten: [
      mitarbeiterColumnDef,
      arbeitsbereichColumnDef,
      arbeitszeitColumnDef,
      dauerColumnDef,
      tooltipsColumnDef,
      editButtonColumnDef,
    ] as ColumnDef<DataTableRow>[],
  };
}

export function useDienstplanColumnDefs({
  switchLoadingStaste,
}: {
  switchLoadingStaste: (id: string) => void;
}): ColumnDef<DienstplanuebernahmeTableRow>[] {
  const { t } = useTranslation();
  const mitarbeiterColumnDef: ColumnDef<DienstplanuebernahmeTableRow> = {
    accessorKey: 'mitarbeiter',
    cell: (props) => {
      const mitarbeiter = props.getValue() as Mitarbeiter;
      return <MitarbeiterCell mitarbeiter={mitarbeiter} />;
    },
    header: ({ column }) => (
      <SortableHeader
        title={t('entities.name_one')}
        sorted={column.getIsSorted()}
      />
    ),
    filterFn: (row, _, filterValue) =>
      filterMitarbeiterName(
        row.original.mitarbeiter.vorname,
        row.original.mitarbeiter.nachname,
        filterValue,
      ),
    sortingFn: (rowA, rowB) => {
      const mitarbeiterNameA =
        rowA.original.mitarbeiter.vorname + rowA.original.mitarbeiter.nachname;
      const mitarbeiternameB =
        rowB.original.mitarbeiter.vorname + rowB.original.mitarbeiter.nachname;
      return mitarbeiterNameA.localeCompare(mitarbeiternameB);
    },
  };

  const arbeitsbereichColumnDef: ColumnDef<DienstplanuebernahmeTableRow> = {
    accessorKey: 'arbeitsbereich',
    cell: (props) => {
      const arbeitsbereich = props.getValue() as Arbeitsbereich;

      return <ArbeitsbereichCell arbeitsbereich={arbeitsbereich} />;
    },

    filterFn: (row, _, filterValue: ArbeitsbereichFilter) => {
      return filterArbeitsbereiche(
        row.original.arbeitsbereich.id,
        row.original.arbeitsbereich.abteilungen,
        filterValue,
        filterValue.arbeitsbereiche,
        filterValue.abteilungen,
      );
    },
    header: ({ column }) => (
      <SortableHeader
        title={t('entities.department_one')}
        sorted={column.getIsSorted()}
      />
    ),
    sortingFn: (rowA, rowB) => {
      const arbeitsbereichA =
        rowA.original.arbeitsbereich.name + rowA.original.arbeitsbereich.id;
      const arbeitsbereichB =
        rowB.original.arbeitsbereich.name + rowB.original.arbeitsbereich.id;
      return arbeitsbereichA.localeCompare(arbeitsbereichB);
    },
  };

  const arbeitszeitColumnDef: ColumnDef<DienstplanuebernahmeTableRow> = {
    accessorKey: 'arbeitszeit',
    cell: (props) => {
      const diensplanuebernahme = props.row
        .original as DienstplanuebernahmeTableRow;
      return (
        <DienstplanuebernahmeArbeitszeit
          beginn={diensplanuebernahme.beginn}
          ende={diensplanuebernahme.ende}
          dauer={diensplanuebernahme.pause}
        />
      );
    },
    header: ({ column }) => (
      <SortableHeader
        title={t('entities.workingHour_one')}
        sorted={column.getIsSorted()}
      />
    ),
    sortingFn: (rowA, rowB) => {
      const beginn = rowA.original.beginn;
      const ende = rowB.original.ende;
      if (ende === null) {
        return -1;
      }
      if (beginn < ende) {
        return -1;
      }
      if (beginn > ende) {
        return 1;
      }
      return 0;
    },
  };

  const dauerColumnDef: ColumnDef<DienstplanuebernahmeTableRow> = {
    accessorKey: 'dauer',
    header: ({ column }) => (
      <SortableHeader
        title={t('entities.duration')}
        sorted={column.getIsSorted()}
      />
    ),
    sortingFn: (rowA, rowB) => {
      const dauerA = rowA.original.dauer;
      const dauerB = rowB.original.dauer;
      if (dauerA < dauerB) {
        return -1;
      }
      if (dauerA > dauerB) {
        return 1;
      }
      return 0;
    },
    cell: (props) => {
      const dienstplanuebernahme = props.row.original;
      return (
        <DauerCell dauer={dienstplanuebernahme.dauer} isBorderVisible={true} />
      );
    },
  };

  const tooltipsColumnDef: ColumnDef<DienstplanuebernahmeTableRow> = {
    accessorKey: 'tooltips',
    header: '',
    cell: (props) => {
      const dienstplanuebernahme = props.row.original;

      return (
        <E2NTableCell className="font-semibold w-auto" isBorderVisible>
          <div className="flex gap-2 justify-between">
            <RechtshinweiseTooltip
              rechtshinweise={{
                rechtshinweise: dienstplanuebernahme.rechtshinweise,
              }}
            />
            <ZuschlaegeTooltip zuschlaege={dienstplanuebernahme.zuschlaege} />
            <TerminTooltip />
            <BemerkungTooltip bemerkung={dienstplanuebernahme.bemerkung} />
          </div>
        </E2NTableCell>
      );
    },
  };

  const editButtonColumnDef: ColumnDef<DienstplanuebernahmeTableRow> = {
    id: 'editButton',
    header: '',
    cell: (props) => {
      const dienstplanuebernahme = props.row
        .original as DienstplanuebernahmeTableRow;

      return (
        <DienstplanuebernahmeEdit
          dienstplanuebernahme={dienstplanuebernahme}
          switchLoadingState={switchLoadingStaste}
        />
      );
    },
  };

  return [
    mitarbeiterColumnDef,
    arbeitsbereichColumnDef,
    arbeitszeitColumnDef,
    dauerColumnDef,
    tooltipsColumnDef,
    editButtonColumnDef,
  ];
}
